import React, { useState, useEffect, useRef } from 'react';
import Confetti from '../ui/Confetti';
import StatBox from '../ui/StatBox';
import useInterval from '../../hooks/useInterval';
import { playCorrectSound, playWrongSound, playGameOverSound, playSuccessSound } from '../../utils/audio';

const WORDS = ['pumpkin', 'apple', 'pie', 'feast', 'leaf', 'corn', 'yam', 'thank', 'family', 'friend', 'cozy', 'brown', 'maize', 'fall', 'cornucopia', 'gobble', 'giving', 'acorn', 'meal', 'wish', 'harvest', 'dinner', 'gravy', 'soft', 'jump', 'fun'];
const LETTERS = ['t', 'g', 'k', 'p', 's', 'a', 'f', 'm', 'v', 'd', 'h', 'w'];
const CHALLENGE_COUNT = 25;
const MAX_MISTAKES = 3;

const shuffleArray = (array: any[]) => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};

const TurkeyChaseGame: React.FC = () => {
    const [gameState, setGameState] = useState<'ready' | 'playing' | 'gameOver' | 'win'>('ready');
    const [challenges, setChallenges] = useState<string[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [mistakes, setMistakes] = useState(0);
    const [typedText, setTypedText] = useState('');
    const [elapsedTime, setElapsedTime] = useState(0);
    const [runnerPosition, setRunnerPosition] = useState(0); // 0-100
    const [isWrong, setIsWrong] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const setupGame = () => {
        const mixedChallenges = shuffleArray([...WORDS, ...LETTERS]);
        setChallenges(mixedChallenges.slice(0, CHALLENGE_COUNT));
        setCurrentIndex(0);
        setMistakes(0);
        setTypedText('');
        setElapsedTime(0);
        setRunnerPosition(0);
    };

    useEffect(setupGame, []);

    useInterval(() => {
        if (gameState === 'playing') {
            setElapsedTime(t => t + 1);
        }
    }, 1000);

    const startGame = () => {
        setupGame();
        setGameState('playing');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const currentChallenge = challenges[currentIndex];

    const handleInput = (value: string) => {
        if (gameState !== 'playing' || !currentChallenge) return;

        setTypedText(value);

        // Auto-check for single-letter challenges
        if (currentChallenge.length === 1 && value.length === 1) {
            if (value === currentChallenge) {
                handleCorrect();
            } else {
                handleIncorrect();
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Check for spacebar or enter submission for words
        if (currentChallenge && currentChallenge.length > 1 && (e.key === ' ' || e.key === 'Enter')) {
            e.preventDefault();
            if (typedText === currentChallenge) {
                handleCorrect();
            } else {
                handleIncorrect();
            }
        }
    };

    const handleCorrect = () => {
        playCorrectSound();
        const newIndex = currentIndex + 1;
        if (newIndex >= challenges.length) {
            setGameState('win');
            playSuccessSound();
        }
        setCurrentIndex(newIndex);
        setRunnerPosition(pos => pos + (100 / CHALLENGE_COUNT));
        setTypedText('');
    };

    const handleIncorrect = () => {
        playWrongSound();
        const newMistakes = mistakes + 1;
        setMistakes(newMistakes);
        setIsWrong(true);
        setTimeout(() => setIsWrong(false), 300);
        
        if (newMistakes >= MAX_MISTAKES) {
            setGameState('gameOver');
            playGameOverSound();
        }
        setTypedText('');
    };

    const renderChallengeText = () => {
        if (!currentChallenge) return null;
        return currentChallenge.split('').map((char, index) => {
            let colorClass = "text-gray-500";
            if (index < typedText.length) {
                colorClass = typedText[index] === char ? 'text-green-600' : 'text-red-500';
            }
            return <span key={index} className={colorClass}>{char}</span>;
        });
    };

    const renderGameStateOverlay = () => {
        switch (gameState) {
            case 'ready':
                return (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center text-white z-10">
                        <h2 className="text-4xl font-bold">Who Will Catch the Turkey?</h2>
                        <p className="mt-2">Type the words and letters to catch the turkey. 3 mistakes and it gets away!</p>
                        <button onClick={startGame} className="mt-6 px-8 py-3 bg-orange-600 text-white font-bold text-xl rounded-lg shadow-lg hover:bg-orange-700 transition">
                            Start Race!
                        </button>
                    </div>
                );
            case 'gameOver':
                return (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center text-white z-10">
                        <h2 className="text-4xl font-bold text-red-500">The turkey got away!</h2>
                        <p className="mt-2">Don't worry, you can try again!</p>
                        <button onClick={startGame} className="mt-6 px-8 py-3 bg-blue-600 text-white font-bold text-xl rounded-lg shadow-lg hover:bg-blue-700 transition">
                            Try Again
                        </button>
                    </div>
                );
            case 'win':
                 return (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center text-white z-10">
                        <Confetti />
                        <h2 className="text-4xl font-bold text-yellow-400">You caught the turkey!</h2>
                        <div className="my-4 text-5xl">🏆</div>
                        <p className="text-xl">You are a Turkey Tamer!</p>
                        <p>Your time: {elapsedTime} seconds</p>
                        <button onClick={startGame} className="mt-6 px-8 py-3 bg-blue-600 text-white font-bold text-xl rounded-lg shadow-lg hover:bg-blue-700 transition">
                            Play Again
                        </button>
                    </div>
                );
            default:
                return null;
        }
    };
    
    // Turkey starts at 80%, runner at 0. The runner needs to close the 80 point gap.
    const turkeyPosition = 80;
    const runnerVisualPosition = (runnerPosition / 100) * turkeyPosition;

    return (
        <div className="w-full relative" onClick={() => inputRef.current?.focus()}>
            {renderGameStateOverlay()}
            <div className="flex flex-wrap justify-around items-center mb-4 p-4 bg-orange-100 rounded-lg">
                <StatBox label="Time" value={elapsedTime} />
                <StatBox label="Mistakes" value={`${mistakes} / ${MAX_MISTAKES}`} />
                <StatBox label="Words Left" value={challenges.length - currentIndex} />
            </div>

            <div className="relative h-32 bg-amber-200 rounded-lg overflow-hidden p-4">
                 <style>{`
                    @keyframes fall { 0% { transform: translateY(-100%); opacity: 0; } 100% { transform: translateY(200px); opacity: 1; } }
                    .leaf { position: absolute; font-size: 24px; animation: fall linear infinite; }
                `}</style>
                <div className="leaf" style={{left: '10%', animationDuration: '5s'}}>🍂</div>
                <div className="leaf" style={{left: '30%', animationDuration: '7s', animationDelay: '2s'}}>🍁</div>
                <div className="leaf" style={{left: '50%', animationDuration: '6s'}}>🍂</div>
                <div className="leaf" style={{left: '70%', animationDuration: '8s', animationDelay: '3s'}}>🍁</div>
                <div className="leaf" style={{left: '90%', animationDuration: '5s', animationDelay: '1s'}}>🍂</div>

                <div className="absolute bottom-4 text-5xl transition-all duration-300" style={{ left: `${runnerVisualPosition}%` }}>
                    <div className="transform scale-x-[-1]">
                      <div className={gameState === 'playing' ? "animate-bounce" : ""}>🏃</div>
                    </div>
                </div>
                <div className="absolute bottom-4 text-5xl transition-all duration-300" style={{ left: `${turkeyPosition}%` }}>
                    <div className={gameState === 'playing' && runnerPosition < 95 ? "animate-pulse" : ""}>🦃</div>
                </div>
            </div>

             <div className={`my-8 p-4 bg-gray-100 rounded-lg text-center font-mono text-5xl tracking-widest h-24 flex items-center justify-center transition-colors ${isWrong ? 'bg-red-200' : ''}`}>
               {renderChallengeText()}
            </div>

            <input
                ref={inputRef}
                type="text"
                value={typedText}
                onChange={e => handleInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="opacity-0 absolute w-0 h-0"
                autoFocus
                disabled={gameState !== 'playing'}
            />
        </div>
    );
};

export default TurkeyChaseGame;